import React, { useEffect, useMemo, useState } from "react";
import { Content } from "antd/es/layout/layout";
import { useLocation, useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { Button, Form, InputNumber, Select, Spin } from "antd";
import {
  ArrowRightOutlined,
  ExclamationCircleFilled,
  LeftOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { getTableID } from "../Services/authServices";
import canadaFlag from "../Image/ca-circle.png";
import barbadosFlag from "../Image/flag/barbados.png";
import walletLogoIcon from "../Image/logo-icon.png";
import {
  getUser,
  userMarkupsByUserID,
  walletsByUserID,
} from "../graphql/queries";
import "./style.css";
import AppActionModal from "../Components/AppActionModal";
import { getVerificationUiState } from "../Utils/verificationStatus";

const client = generateClient();

const SOURCE_OF_FUNDS_OPTIONS = [
  "Salary / Income",
  "Savings",
  "Pension / Retirement",
  "Borrowed Funds / Loan",
  "Gift",
  "Welfare",
  "Inheritance",
  "Charitable donations",
  "Other",
];

const SENDING_PURPOSE_OPTIONS = [
  "Family support / living expenses",
  "Education",
  "Medical expenses",
  "Property or housing expenses",
  "Business payment",
  "Investment",
  "Travel expenses",
  "Charity / donation",
];

const formatRate = (value) => Number(value || 0).toFixed(4);
const MAX_SOURCE_WHOLE_DIGITS = 9;
const MAX_CONVERTED_WHOLE_DIGITS = 10;

const sanitizeAmountInput = (
  value,
  maxWholeDigits = MAX_SOURCE_WHOLE_DIGITS,
) => {
  if (!value) return "";
  let cleaned = String(value)
    .replace(/,/g, "")
    .replace(/[^\d.]/g, "");
  const firstDotIndex = cleaned.indexOf(".");
  if (firstDotIndex !== -1) {
    cleaned =
      cleaned.slice(0, firstDotIndex + 1) +
      cleaned.slice(firstDotIndex + 1).replace(/\./g, "");
  }

  const [wholePart = "", decimalPart = ""] = cleaned.split(".");
  const whole = wholePart.slice(0, maxWholeDigits);
  const decimal = decimalPart.slice(0, 2);
  return cleaned.includes(".") ? `${whole}.${decimal}` : whole;
};

const formatAmountInput = (
  value,
  info,
  maxWholeDigits = MAX_SOURCE_WHOLE_DIGITS,
) => {
  if (value === null || value === undefined || value === "") return "";
  const sanitized = sanitizeAmountInput(value, maxWholeDigits);
  if (!sanitized) return "";

  if (info?.userTyping) {
    const [whole, decimal] = sanitized.split(".");
    const wholeWithCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimal !== undefined
      ? `${wholeWithCommas}.${decimal}`
      : wholeWithCommas;
  }

  const numeric = Number(sanitized);
  if (Number.isNaN(numeric)) return "";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const normalizeAmountValue = (
  value,
  maxWholeDigits = MAX_SOURCE_WHOLE_DIGITS,
) => {
  const sanitized = sanitizeAmountInput(value, maxWholeDigits).replace(
    /\.$/,
    "",
  );
  if (!sanitized) return null;
  const numeric = Number(sanitized);
  return Number.isFinite(numeric) ? numeric : null;
};

const validatePositiveAmount = (_, value) =>
  normalizeAmountValue(value) > 0
    ? Promise.resolve()
    : Promise.reject(new Error("Amount must be greater than 0."));

const handleAmountKeyDown = (
  event,
  maxWholeDigits = MAX_SOURCE_WHOLE_DIGITS,
) => {
  if (["e", "E", "+", "-"].includes(event.key)) {
    event.preventDefault();
    return;
  }

  const { key, target } = event;
  const inputValue = String(target?.value || "");
  const raw = inputValue.replace(/,/g, "");
  const dotIndex = raw.indexOf(".");
  const [whole = "", decimal = ""] = raw.split(".");

  if (key === ".") {
    if (dotIndex !== -1) event.preventDefault();
    return;
  }

  if (!/^\d$/.test(key)) return;

  const selectionStart = target?.selectionStart ?? inputValue.length;
  const selectionEnd = target?.selectionEnd ?? inputValue.length;
  if (selectionStart !== selectionEnd) return;

  const rawCursorPos = inputValue
    .slice(0, selectionStart)
    .replace(/,/g, "").length;
  const isEditingDecimal = dotIndex !== -1 && rawCursorPos > dotIndex;

  if (!isEditingDecimal && whole.length >= maxWholeDigits) {
    event.preventDefault();
    return;
  }

  if (isEditingDecimal && decimal.length >= 2) {
    event.preventDefault();
  }
};

const sanitizeSourceAmountInput = (value) =>
  sanitizeAmountInput(value, MAX_SOURCE_WHOLE_DIGITS);
const sanitizeConvertedAmountInput = (value) =>
  sanitizeAmountInput(value, MAX_CONVERTED_WHOLE_DIGITS);
const formatSourceAmountInput = (value, info) =>
  formatAmountInput(value, info, MAX_SOURCE_WHOLE_DIGITS);
const formatConvertedAmountInput = (value, info) =>
  formatAmountInput(value, info, MAX_CONVERTED_WHOLE_DIGITS);
const normalizeSourceAmountValue = (value) =>
  normalizeAmountValue(value, MAX_SOURCE_WHOLE_DIGITS);
const normalizeConvertedAmountValue = (value) =>
  normalizeAmountValue(value, MAX_CONVERTED_WHOLE_DIGITS);
const handleSourceAmountKeyDown = (event) =>
  handleAmountKeyDown(event, MAX_SOURCE_WHOLE_DIGITS);
const handleConvertedAmountKeyDown = (event) =>
  handleAmountKeyDown(event, MAX_CONVERTED_WHOLE_DIGITS);

const getCurrencyFlag = (currencyCode) =>
  String(currencyCode || "").toUpperCase() === "CAD"
    ? canadaFlag
    : barbadosFlag;

const getRecipientCurrency = (recipient) => {
  const walletCurrency = String(
    recipient?.RecipientWallet?.currency || "",
  ).toUpperCase();
  if (walletCurrency === "CAD" || walletCurrency === "BBD")
    return walletCurrency;
  return recipient?.addressCountry === "Canada" ? "CAD" : "BBD";
};

const SendMoney = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const recipient = state?.recipient;
  const sourceCurrency = String(state?.currencyNow || "CAD").toUpperCase();
  const targetCurrency = String(
    state?.targetCurrency ||
      state?.currencyNow2 ||
      getRecipientCurrency(recipient),
  ).toUpperCase();
  const isSameCurrencyTransfer = sourceCurrency === targetCurrency;
  const cameFromRecipientSelection = state?.from === "recipientSelection";
  const [form] = Form.useForm();
  const [cadToBBDRate, setCadToBBDRate] = useState(0);
  const [bbdToCADRate, setBBDToCADRate] = useState(0);
  const [walletTransactionFeeBBD, setWalletTransactionFeeBBD] = useState(0);
  const [walletTransactionFeeCAD, setWalletTransactionFeeCAD] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState({
    identificationVerified: false,
    verificationStatus: "",
    isOrganization: false,
  });
  const [showModal, setShowModal] = useState(false);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [showRateExpiredModal, setShowRateExpiredModal] = useState(false);
  const [sendAmount, setSendAmount] = useState(null);
  const [receiveAmount, setReceiveAmount] = useState(null);
  const [lastEditedField, setLastEditedField] = useState("send");
  const [refreshingRate, setRefreshingRate] = useState(false);
  const [seconds, setSeconds] = useState(state?.selectTime || 300);
  const [showRateInLockPill, setShowRateInLockPill] = useState(true);
  const [wallet, setWallet] = useState({
    id: "",
    currency: sourceCurrency,
    availableBalance: 0,
  });
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [remittanceLimits, setRemittanceLimits] = useState({
    remainingDaily: null,
    remainingWeekly: null,
  });
  const [limitError, setLimitError] = useState(null);
  const exchangeRate = isSameCurrencyTransfer
    ? 1
    : sourceCurrency === "BBD"
      ? bbdToCADRate
      : cadToBBDRate;
  const verificationUi = useMemo(
    () =>
      getVerificationUiState({
        verificationStatus: verificationStatus.verificationStatus,
        identificationVerified: verificationStatus.identificationVerified,
        isOrganization: verificationStatus.isOrganization,
      }),
    [verificationStatus],
  );

  const formattedTime = useMemo(() => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  }, [seconds]);

  const revalidateFieldIfNeeded = async (fieldName) => {
    if (!form.getFieldError(fieldName)?.length) return;
    try {
      await form.validateFields([fieldName]);
    } catch (error) {
      // Keep existing error state until field is valid.
    }
  };

  const fetchRate = async () => {
    try {
      const tableID = await getTableID();
      const adminQuery = `query MyQuery {
        listAdmins {
          items {
            finalCADToBBD
            finalBBDToCAD
          }
        }
      }`;
      const [adminResponse, markupResponse] = await Promise.all([
        client.graphql({ query: adminQuery }),
        tableID
          ? client.graphql({
              query: userMarkupsByUserID,
              variables: { userID: tableID },
            })
          : Promise.resolve(null),
      ]);

      const adminItem = adminResponse?.data?.listAdmins?.items?.[0];
      const finalCADToBBD = Number(adminItem?.finalCADToBBD || 0);
      const finalBBDToCAD = Number(adminItem?.finalBBDToCAD || 0);

      const markupItem = markupResponse?.data?.userMarkupsByUserID?.items?.[0];
      const markUpRateCAD = Number(markupItem?.markUpRateCAD || 0);
      const markUpRateBBD = Number(markupItem?.markUpRateBBD || 0);
      const feeBBD = Number(markupItem?.walletTransactionFeeBBD || 0);
      const feeCAD = Number(markupItem?.walletTransactionFeeCAD || 0);

      const cadToBBD = finalCADToBBD * (1 - markUpRateCAD / 100);
      if (cadToBBD > 0) setCadToBBDRate(cadToBBD);

      const bbdToCAD = finalBBDToCAD * (1 - markUpRateBBD / 100);
      if (bbdToCAD > 0) setBBDToCADRate(bbdToCAD);
      setWalletTransactionFeeBBD(feeBBD);
      setWalletTransactionFeeCAD(feeCAD);
    } catch (error) {
      console.error("Error fetching exchange rate:", error);
    }
  };

  const handleRefreshRate = async () => {
    try {
      setRefreshingRate(true);
      await fetchRate();
      setSeconds(300);
      setShowRateExpiredModal(false);
    } finally {
      setRefreshingRate(false);
    }
  };

  useEffect(() => {
    if (isSameCurrencyTransfer) return;

    const interval = setInterval(() => {
      setSeconds((prevSeconds) => {
        if (prevSeconds <= 1) {
          clearInterval(interval);
          setShowRateExpiredModal(true);
          return 0;
        }
        return prevSeconds - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isSameCurrencyTransfer]);

  useEffect(() => {
    if (isSameCurrencyTransfer) return;

    const interval = setInterval(() => {
      setShowRateInLockPill((prev) => !prev);
    }, 10000);

    return () => clearInterval(interval);
  }, [isSameCurrencyTransfer]);

  useEffect(() => {
    const fetchVerificationStatus = async () => {
      const tableID = await getTableID();
      if (!tableID) return;
      const [{ data }, walletResponse] = await Promise.all([
        client.graphql({
          query: getUser,
          variables: { id: tableID },
        }),
        client.graphql({
          query: walletsByUserID,
          variables: { userID: tableID, limit: 1 },
        }),
      ]);
      const status = data?.getUser;
      if (status) {
        setVerificationStatus({
          identificationVerified: !!status.identificationVerified,
          verificationStatus: status.verificationStatus || "",
          isOrganization: !!status.isOrganization,
        });
        setRemittanceLimits({
          remainingDaily: status.remainingDailyRemittanceAmount ?? null,
          remainingWeekly: status.remainingWeeklyRemittanceAmount ?? null,
        });
      }

      const walletItem = walletResponse?.data?.walletsByUserID?.items?.[0];
      if (walletItem) {
        setWallet({
          id: walletItem.id || "",
          currency: String(walletItem.currency || sourceCurrency).toUpperCase(),
          availableBalance: Number(walletItem.availableBalance || 0),
        });
      }
      setLoadingWallet(false);
    };

    fetchRate();
    fetchVerificationStatus();
  }, [sourceCurrency]);

  useEffect(() => {
    if (!state) return;

    const initialSendAmount =
      state.formattedSendRate !== undefined && state.formattedSendRate !== null
        ? Number(state.formattedSendRate)
        : state.sendRate !== undefined && state.sendRate !== null
          ? Number(state.sendRate)
          : null;

    const initialReceiveAmount =
      state.getRate !== undefined && state.getRate !== null
        ? Number(state.getRate)
        : null;

    if (initialSendAmount !== null && !Number.isNaN(initialSendAmount)) {
      setSendAmount(initialSendAmount);
      form.setFieldValue("sendAmount", initialSendAmount);
      setLastEditedField("send");
    }

    if (initialReceiveAmount !== null && !Number.isNaN(initialReceiveAmount)) {
      setReceiveAmount(Number(initialReceiveAmount.toFixed(2)));
    }

    form.setFieldsValue({
      sourceOfFund: state.sourceOfFund,
      sendingPurpose: state.selectedReasons,
    });
  }, [form, state]);

  useEffect(() => {
    if (!exchangeRate) return;
    if (
      lastEditedField === "send" &&
      sendAmount !== null &&
      sendAmount !== undefined
    ) {
      setReceiveAmount(Number((Number(sendAmount) * exchangeRate).toFixed(2)));
    } else if (
      lastEditedField === "receive" &&
      receiveAmount !== null &&
      receiveAmount !== undefined
    ) {
      setSendAmount(Number((Number(receiveAmount) / exchangeRate).toFixed(2)));
    }
  }, [exchangeRate, lastEditedField, receiveAmount, sendAmount]);

  const handleSendAmountChange = (value) => {
    const normalizedValue = normalizeSourceAmountValue(value);
    setLastEditedField("send");
    setLimitError(null);
    setSendAmount(normalizedValue);
    form.setFieldValue("sendAmount", normalizedValue);
    if (normalizedValue === null) {
      setReceiveAmount(null);
      revalidateFieldIfNeeded("sendAmount");
      return;
    }
    setReceiveAmount(Number((normalizedValue * exchangeRate).toFixed(2)));
    revalidateFieldIfNeeded("sendAmount");
  };

  const handleReceiveAmountChange = (value) => {
    const normalizedValue = normalizeConvertedAmountValue(value);
    setLastEditedField("receive");
    setLimitError(null);
    setReceiveAmount(normalizedValue);
    if (normalizedValue === null) {
      setSendAmount(null);
      form.setFieldValue("sendAmount", null);
      revalidateFieldIfNeeded("sendAmount");
      return;
    }
    if (exchangeRate > 0) {
      const nextSendAmount = Number(
        (normalizedValue / exchangeRate).toFixed(2),
      );
      setSendAmount(nextSendAmount);
      form.setFieldValue("sendAmount", nextSendAmount);
      revalidateFieldIfNeeded("sendAmount");
    }
  };

  const recipientDisplayName =
    `${recipient?.firstName || "Recipient"} ${recipient?.lastName || ""}`.trim();
  const formatNumber = (num) =>
    Number(num || 0)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const requiredAmount = Number(sendAmount || 0);
  const availableBalance = Number(wallet.availableBalance || 0);
  const shortfallAmount = Math.max(requiredAmount - availableBalance, 0);
  const hasSufficientBalance =
    availableBalance >= requiredAmount && requiredAmount > 0;
  const amountExceedsBalance =
    !loadingWallet && requiredAmount > 0 && requiredAmount > availableBalance;

  const handleContinue = async () => {
    try {
      const values = await form.validateFields([
        "sendAmount",
        "sourceOfFund",
        "sendingPurpose",
      ]);
      if (!verificationUi.isVerified) {
        setShowModal(true);
        return;
      }

      const amountToSend = normalizeAmountValue(values.sendAmount) || 0;
      if (
        remittanceLimits.remainingDaily !== null &&
        amountToSend > remittanceLimits.remainingDaily
      ) {
        setLimitError(
          `This transfer exceeds your remaining daily remittance limit of $${formatNumber(remittanceLimits.remainingDaily)} ${sourceCurrency}.`,
        );
        return;
      }
      if (
        remittanceLimits.remainingWeekly !== null &&
        amountToSend > remittanceLimits.remainingWeekly
      ) {
        setLimitError(
          `This transfer exceeds your remaining weekly remittance limit of $${formatNumber(remittanceLimits.remainingWeekly)} ${sourceCurrency}.`,
        );
        return;
      }
      setLimitError(null);
      if (!isSameCurrencyTransfer && seconds <= 0) {
        setShowRateExpiredModal(true);
        return;
      }

      if (!hasSufficientBalance) {
        return;
      }

      const formattedSendRate = Number(
        normalizeSourceAmountValue(values.sendAmount) || 0,
      ).toFixed(2);
      navigate("/overview", {
        state: {
          recipient,
          currencyNow: sourceCurrency.toLowerCase(),
          currencyNow2: targetCurrency.toLowerCase(),
          targetCurrency,
          formattedSendRate,
          getRate: Number(
            isSameCurrencyTransfer ? values.sendAmount : receiveAmount || 0,
          ),
          cadToBBDRate: exchangeRate,
          bBDToCADSRate: exchangeRate > 0 ? 1 / exchangeRate : 0,
          selectedReasons: values.sendingPurpose,
          sourceOfFund: values.sourceOfFund,
          selectedPaymentMethod: "wallet",
          selectTime: isSameCurrencyTransfer ? null : seconds,
        },
      });
    } catch (error) {
      // Antd form handles field messages.
    }
  };

  const navigateToAddFunds = async () => {
    const values = form.getFieldsValue([
      "sendAmount",
      "sourceOfFund",
      "sendingPurpose",
    ]);

    navigate("/fundWallet", {
      state: {
        wallet,
        fundingContext: "transfer",
        fundingShortfallAmount: shortfallAmount,
        preferredPaymentMethod: "Interac",
        returnState: {
          recipient,
          currencyNow: sourceCurrency.toLowerCase(),
          currencyNow2: targetCurrency.toLowerCase(),
          targetCurrency,
          formattedSendRate: Number(
            normalizeSourceAmountValue(values.sendAmount ?? sendAmount) || 0,
          ).toFixed(2),
          getRate: Number(
            isSameCurrencyTransfer
              ? normalizeSourceAmountValue(values.sendAmount ?? sendAmount) || 0
              : receiveAmount || 0,
          ),
          cadToBBDRate: exchangeRate,
          bBDToCADSRate: exchangeRate > 0 ? 1 / exchangeRate : 0,
          selectedReasons: values.sendingPurpose,
          sourceOfFund: values.sourceOfFund,
          selectedPaymentMethod: "wallet",
        },
      },
    });
  };

  const handleAddFunds = async () => {
    setShowAddFundsModal(true);
  };

  const handleVerificationSubmit = () => {
    navigate("/kycuser");
  };

  if (!recipient) {
    return (
      <Content className="appPageContent">
        <div className="appPageShell">
          <div className="appSectionHeader">
            <h2 className="appSectionTitle">Send Money</h2>
          </div>
          <div className="appTableCard">
            <p>Recipient data is not available.</p>
            <Button
              type="primary"
              onClick={() =>
                navigate("/recipients", { state: { flow: "sendMoney" } })
              }
            >
              Back to Recipients
            </Button>
          </div>
        </div>
      </Content>
    );
  }

  return (
    <Content className="appPageContent">
      <div className="appPageShell appPageShellNarrow">
        <div className="appSectionHeader appPageHeader">
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() => {
              if (cameFromRecipientSelection) {
                navigate("/recipients", { state: { flow: "sendMoney" } });
                return;
              }
              navigate("/recipientDetail", { state: { recipient } });
            }}
            className="recipientDetailBackBtn"
          />
          <h2 className="appSectionTitle">Send Money</h2>
        </div>

        <div className="appTableCard sendMoneyV2Card">
          <Form
            layout="vertical"
            form={form}
            requiredMark={false}
            validateTrigger="onSubmit"
          >
            <div className="sendMoneyV2Section">
              <div className="sendMoneyV2AmountCard">
                <div className="sendMoneyV2AmountWrap">
                  {isSameCurrencyTransfer ? (
                    <div className="sendMoneyV2AmountPanel sendMoneyV2AmountPanelSend sendMoneyV2AmountPanelSingle">
                      <Form.Item
                        label="Amount"
                        name="sendAmount"
                        rules={[
                          {
                            required: true,
                            validator: validatePositiveAmount,
                          },
                        ]}
                      >
                        <div
                          className={`dashboardConverterInputRowV2 sendMoneySameCurrencyRow ${
                            amountExceedsBalance
                              ? "sendMoneyAmountExceeded"
                              : ""
                          }`}
                        >
                          <span className="dashboardConverterCurrencyV2">
                            <img
                              src={getCurrencyFlag(sourceCurrency)}
                              alt={sourceCurrency}
                              className="dashboardCurrencyFlag"
                            />
                            <span className="dashboardConverterCurrencyText">
                              {sourceCurrency}
                            </span>
                          </span>
                          <InputNumber
                            className="dashboardAmountInput dashboardConverterInput sendMoneySameCurrencyInput"
                            min={0}
                            size="large"
                            placeholder="0.00"
                            value={sendAmount}
                            onChange={handleSendAmountChange}
                            formatter={formatSourceAmountInput}
                            parser={sanitizeSourceAmountInput}
                            onKeyDown={handleSourceAmountKeyDown}
                            style={{ width: "100%" }}
                          />
                          {amountExceedsBalance ? (
                            <span
                              className="sendMoneyAmountWarningIcon"
                              aria-hidden="true"
                            >
                              <ExclamationCircleFilled />
                            </span>
                          ) : null}
                        </div>
                      </Form.Item>
                    </div>
                  ) : (
                    <>
                      <div className="sendMoneyV2RatePillRow">
                        <div className="sendMoneyV2RateRowLabel">
                          EXCHANGE RATE
                        </div>
                        <div className="sendMoneyV2RateActions">
                          <span className="sendMoneyRateLockComboPill">
                            <span
                              className="sendMoneyRateLockComboIcon"
                              aria-hidden="true"
                            >
                              <LockOutlined />
                            </span>
                            <span
                              className="sendMoneyRateLockComboIconDivider"
                              aria-hidden="true"
                            />
                            <span className="sendMoneyRateLockComboContent">
                              <span
                                className={`sendMoneyRateLockComboFace ${
                                  showRateInLockPill
                                    ? "sendMoneyRateLockComboFaceActive"
                                    : ""
                                }`}
                              >
                                {`1 ${sourceCurrency} = ${formatRate(exchangeRate)} ${targetCurrency}`}
                              </span>
                              <span
                                className={`sendMoneyRateLockComboFace ${
                                  !showRateInLockPill
                                    ? "sendMoneyRateLockComboFaceActive"
                                    : ""
                                }`}
                              >
                                {`Rate locked ${formattedTime}`}
                              </span>
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="sendMoneyV2AmountGrid">
                        <div className="sendMoneyV2AmountPanel sendMoneyV2AmountPanelSend">
                          <Form.Item
                            label="You send"
                            name="sendAmount"
                            rules={[
                              {
                                required: true,
                                validator: validatePositiveAmount,
                              },
                            ]}
                          >
                            <div
                              className={`dashboardConverterInputRowV2 sendMoneySameCurrencyRow ${
                                amountExceedsBalance
                                  ? "sendMoneyAmountExceeded"
                                  : ""
                              }`}
                            >
                              <span className="dashboardConverterCurrencyV2">
                                <img
                                  src={getCurrencyFlag(sourceCurrency)}
                                  alt={sourceCurrency}
                                  className="dashboardCurrencyFlag"
                                />
                                <span className="dashboardConverterCurrencyText">
                                  {sourceCurrency}
                                </span>
                              </span>
                              <InputNumber
                                className="dashboardAmountInput dashboardConverterInput sendMoneySameCurrencyInput"
                                min={0}
                                size="large"
                                placeholder="0.00"
                                value={sendAmount}
                                onChange={handleSendAmountChange}
                                formatter={formatSourceAmountInput}
                                parser={sanitizeSourceAmountInput}
                                onKeyDown={handleSourceAmountKeyDown}
                                style={{ width: "100%" }}
                              />
                              {amountExceedsBalance ? (
                                <span
                                  className="sendMoneyAmountWarningIcon"
                                  aria-hidden="true"
                                >
                                  <ExclamationCircleFilled />
                                </span>
                              ) : null}
                            </div>
                          </Form.Item>
                        </div>

                        <div className="sendMoneyV2Arrow" aria-hidden="true">
                          <div className="sendMoneyV2ArrowBadge">
                            <ArrowRightOutlined />
                          </div>
                        </div>

                        <div className="sendMoneyV2AmountPanel sendMoneyV2AmountPanelReceive">
                          <Form.Item label={`${recipientDisplayName} gets`}>
                            <div className="dashboardConverterInputRowV2 sendMoneySameCurrencyRow">
                              <span className="dashboardConverterCurrencyV2">
                                <img
                                  src={getCurrencyFlag(targetCurrency)}
                                  alt={targetCurrency}
                                  className="dashboardCurrencyFlag"
                                />
                                <span className="dashboardConverterCurrencyText">
                                  {targetCurrency}
                                </span>
                              </span>
                              <InputNumber
                                className="dashboardAmountInput dashboardConverterInput sendMoneySameCurrencyInput"
                                value={receiveAmount}
                                min={0}
                                size="large"
                                placeholder="0.00"
                                onChange={handleReceiveAmountChange}
                                formatter={formatConvertedAmountInput}
                                parser={sanitizeConvertedAmountInput}
                                onKeyDown={handleConvertedAmountKeyDown}
                                style={{ width: "100%" }}
                              />
                            </div>
                          </Form.Item>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="sendMoneyWalletBalanceRow">
                    {loadingWallet ? (
                      <div className="sendMoneyWalletBalanceLoading">
                        <Spin size="small" />
                      </div>
                    ) : (
                      <>
                        <div className="sendMoneyWalletBalanceInfo">
                          <span className="sendMoneyWalletBalanceIconWrap">
                            <img
                              src={walletLogoIcon}
                              alt="BajCan Wallet"
                              className="sendMoneyWalletBalanceIcon"
                            />
                          </span>
                          <span className="sendMoneyWalletBalanceLabel">
                            Available balance
                          </span>
                          <strong className="sendMoneyWalletBalanceValue">
                            ${formatNumber(availableBalance)} {wallet.currency}
                          </strong>
                        </div>
                      </>
                    )}
                  </div>

                  {!loadingWallet &&
                  !hasSufficientBalance &&
                  requiredAmount > 0 ? (
                    <div className="sendMoneyAddFundsInlineAlert">
                      <div className="sendMoneyAddFundsInlineText">
                        <strong>
                          You do not have enough balance to complete this
                          transfer.
                        </strong>
                      </div>
                      <Button
                        type="default"
                        size="small"
                        className="sendMoneyAddFundsInlineBtn"
                        onClick={handleAddFunds}
                      >
                        Request Add Funds
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="sendMoneyV2Section">
              <h3 className="sendMoneyV2SectionTitle">Source of Funds</h3>
              <Form.Item
                name="sourceOfFund"
                rules={[
                  { required: true, message: "Please select source of funds." },
                ]}
              >
                <Select
                  className="sendMoneyV2Select"
                  size="large"
                  placeholder="Select source of funds"
                  onChange={() => revalidateFieldIfNeeded("sourceOfFund")}
                  options={SOURCE_OF_FUNDS_OPTIONS.map((item) => ({
                    value: item,
                    label: item,
                  }))}
                />
              </Form.Item>
            </div>

            <div className="sendMoneyV2Section sendMoneyV2SectionLast">
              <h3 className="sendMoneyV2SectionTitle">Sending Purpose</h3>
              <Form.Item
                name="sendingPurpose"
                rules={[
                  {
                    required: true,
                    message: "Please select a sending purpose.",
                  },
                ]}
              >
                <Select
                  className="sendMoneyV2Select"
                  size="large"
                  placeholder="Select sending purpose"
                  onChange={() => revalidateFieldIfNeeded("sendingPurpose")}
                  options={SENDING_PURPOSE_OPTIONS.map((item) => ({
                    value: item,
                    label: item,
                  }))}
                />
              </Form.Item>
            </div>

            {limitError ? (
              <div className="sendMoneyAddFundsInlineAlert">
                <div className="sendMoneyAddFundsInlineText">{limitError}</div>
              </div>
            ) : null}

            <Form.Item className="sendMoneyV2Actions">
              <Button
                type="primary"
                className="signupbtn"
                size="large"
                onClick={handleContinue}
                block
                disabled={!hasSufficientBalance}
              >
                Next
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>

      <AppActionModal
        open={showModal}
        onCancel={() => setShowModal(false)}
        variant="warning"
        icon={<ExclamationCircleFilled />}
        title={verificationUi?.modalTitle || "Complete Verification"}
        description={
          verificationUi?.modalText ||
          "Please complete KYC/KYB to continue with this transfer."
        }
        secondaryAction={{
          label: verificationUi?.modalSecondaryLabel || "Cancel",
          onClick: () => setShowModal(false),
        }}
        primaryAction={
          verificationUi?.showModalPrimary
            ? {
                label:
                  verificationUi?.modalPrimaryLabel || "Start Verification",
                onClick: handleVerificationSubmit,
              }
            : null
        }
      />

      <AppActionModal
        open={showRateExpiredModal}
        onCancel={() => setShowRateExpiredModal(false)}
        variant="warning"
        icon={<ExclamationCircleFilled />}
        title="Rate Expired"
        description="Your exchange rate has expired. Refresh the rate to continue."
        secondaryAction={{
          label: "Cancel",
          onClick: () => navigate("/dashboard"),
        }}
        primaryAction={{
          label: "Refresh Rate",
          onClick: handleRefreshRate,
        }}
      />

      <AppActionModal
        open={showAddFundsModal}
        onCancel={() => setShowAddFundsModal(false)}
        className="sendMoneyAddFundsModal"
        variant="warning"
        icon={<ExclamationCircleFilled />}
        title="Request Add Funds"
        description="Please note that exchange rates may change while your add funds request is being processed."
        secondaryAction={{
          label: "Cancel",
          onClick: () => setShowAddFundsModal(false),
        }}
        primaryAction={{
          label: "Continue",
          onClick: async () => {
            setShowAddFundsModal(false);
            await navigateToAddFunds();
          },
        }}
      />
    </Content>
  );
};

export default SendMoney;
